import React, { type JSX } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { openDocumentPreview } from "../../DocumentPreviewSlice_Store";
import { setLayoutMode } from "../../LayoutSlice_Store";
import { canPreviewDocument } from "../../services/documentPreviewService";
import { toPreviewDocument, type DocumentData } from "./dealTypes";

/** The Documents tab of the deal details modal. */
export default function DealDocumentsTab({
  documents,
  onClose,
}: {
  documents: DocumentData[];
  onClose: () => void;
}): JSX.Element {
  const dispatch = useAppDispatch();

  const openDocument = (document: DocumentData): void => {
    const documentForPreview = toPreviewDocument(document);
    if (!canPreviewDocument(documentForPreview)) return;
    dispatch(openDocumentPreview(documentForPreview));
    dispatch(setLayoutMode("workspace"));
    onClose(); // Close the modal when opening document preview
  };

  if (documents.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-gray-500">
          No documents available for this deal.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Document Name
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {documents.map((document) => (
              <tr
                key={document.docId}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => openDocument(document)}
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                    {document.originalFilename || document.documentName || "Untitled Document"}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
